import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UsersIcon, 
  DocumentTextIcon, 
  ClipboardDocumentListIcon, 
  DocumentArrowUpIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  TruckIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
//import { toast, ToastContainer } from 'react-toastify';
import toast from '../utils/toast';
import 'react-toastify/dist/ReactToastify.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemStatus, setSystemStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [indiaPostStats, setIndiaPostStats] = useState(null);
  const [courtCasesStats, setCourtCasesStats] = useState(null);
  const [specialStatsLoading, setSpecialStatsLoading] = useState(true);

  // Test toast function

  useEffect(() => {
    fetchDashboardData();
    fetchIndiaPostAndCourtCasesStats();
    // Test toast functionality
  }, []);  

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        toast.error('Please login to view dashboard');
        console.log('Toast error called');
        return;
      }

      console.log('Fetching dashboard data...');
      
      const [statsResponse, activitiesResponse, statusResponse] = await Promise.all([
        api.get('/dashboard/stats').catch(error => {
          console.error('Error fetching stats:', error);
          throw error;
        }),
        api.get('/dashboard/activities').catch(error => {
          console.error('Error fetching activities:', error);
          throw error;
        }),
        api.get('/dashboard/status').catch(error => {
          console.error('Error fetching status:', error);
          throw error;
        })
      ]);

      console.log('Dashboard data fetched successfully');
      toast.success('Dashboard loaded successfully!');
      console.log('Toast success called');

      // Transform stats data to match component structure
      const transformedStats = statsResponse.data.stats.map(stat => ({
        ...stat,
        icon: getIconForStat(stat.name),
        color: stat.color
      }));

      setStats(transformedStats);
      setRecentActivities(activitiesResponse.data);
      setSystemStatus(statusResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Don't show logout toast for dashboard errors, just show a generic error
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
        console.log('Toast 401 error called');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view the dashboard.');
        console.log('Toast 403 error called');
      } else {
        toast.error('Failed to load dashboard data. Please try again.');
        console.log('Toast generic error called');
      }
      
      // Set empty data to prevent loading state
      setStats([]);
      setRecentActivities([]);
      setSystemStatus({});
    } finally {
      setLoading(false);
    }
  };

  const fetchIndiaPostAndCourtCasesStats = async () => {
    try {
      setSpecialStatsLoading(true);
      console.log('Fetching India Post and Court Cases stats...');
      
      const response = await api.get('/dashboard/india-post-court-cases');
      
      setIndiaPostStats(response.data.indiaPost);
      setCourtCasesStats(response.data.courtCases);
      
      console.log('India Post and Court Cases stats fetched successfully');
    } catch (error) {
      console.error('Error fetching India Post and Court Cases stats:', error);
      setIndiaPostStats(null);
      setCourtCasesStats(null);
    } finally {
      setSpecialStatsLoading(false);
    }
  };

  const getIconForStat = (statName) => {
    switch (statName) {
      case 'Total Users':
        return UsersIcon;
      case 'Active Forms':
        return DocumentTextIcon;
      case 'Pending Tasks':
        return ClipboardDocumentListIcon;
      case 'Form Submissions':
        return DocumentArrowUpIcon;
      default:
        return ChartBarIcon;
    }
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'form_submission':
        return DocumentArrowUpIcon;
      case 'user_registration':
        return UsersIcon;
      case 'task_created':
      case 'task_completed':
        return ClipboardDocumentListIcon;
      case 'invoice_generated':
        return DocumentTextIcon;
      default:
        return ChartBarIcon;
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'create-form':
        navigate('/admin/forms');
        break;
      case 'add-user':
        navigate('/admin/users');
        break;
      case 'create-task':
        navigate('/tasks');
        break;
      case 'view-submissions':
        navigate('/form-submissions');
        break;
      case 'test-toast':
        toast.success('Toast notification is working!');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-body">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Welcome back!</h1>
              <p className="mt-2 text-gray-600">Here's what's happening with your data management system today.</p>
            </div>
            <div className="hidden sm:block">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <ClockIcon className="h-4 w-4" />
                <span>Last updated: {new Date().toLocaleTimeString()}</span>
                
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="card hover:shadow-medium transition-all duration-300">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${
                        stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">from last month</span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-full ${stat.color} bg-opacity-10`}>
                    <Icon className={`h-6 w-6 ${stat.color.replace('bg-', 'text-')}`} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* India Post and Court Cases Stats */}
      {!specialStatsLoading && (indiaPostStats || courtCasesStats) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* India Post Stats Card */}
          {indiaPostStats && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TruckIcon className="h-6 w-6 text-blue-600" />
                    <h2 className="text-lg font-semibold text-gray-900">India Post Consignments</h2>
                  </div>
                  <button
                    onClick={() => navigate('/new/india-post')}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Details →
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Total Consignments</span>
                    <span className="text-2xl font-bold text-blue-600">{indiaPostStats.totalConsignments}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">With Detail</span>
                    <span className="text-2xl font-bold text-green-600">{indiaPostStats.consignmentsWithDetail}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Without Detail</span>
                    <span className="text-2xl font-bold text-gray-600">{indiaPostStats.consignmentsWithoutDetail}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Court Cases Stats Card */}
          {courtCasesStats && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ScaleIcon className="h-6 w-6 text-purple-600" />
                    <h2 className="text-lg font-semibold text-gray-900">New Court Cases</h2>
                  </div>
                  <button
                    onClick={() => navigate('/new/court-cases')}
                    className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                  >
                    View Details →
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  {/* Ecourt Stats */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Ecourt</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Settings</p>
                        <p className="text-lg font-bold text-blue-600">{courtCasesStats.ecourt.settingsCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Numbers</p>
                        <p className="text-lg font-bold text-green-600">{courtCasesStats.ecourt.numberSettingsCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Results</p>
                        <p className="text-lg font-bold text-purple-600">{courtCasesStats.ecourt.resultsCount}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hcservices Stats */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Hcservices</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Settings</p>
                        <p className="text-lg font-bold text-blue-600">{courtCasesStats.hcservices.settingsCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Numbers</p>
                        <p className="text-lg font-bold text-green-600">{courtCasesStats.hcservices.numberSettingsCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Results</p>
                        <p className="text-lg font-bold text-purple-600">{courtCasesStats.hcservices.resultsCount}</p>
                      </div>
                    </div>
                  </div>

                  {/* Scigov Stats */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Scigov</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Settings</p>
                        <p className="text-lg font-bold text-blue-600">{courtCasesStats.scigov.settingsCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Numbers</p>
                        <p className="text-lg font-bold text-green-600">{courtCasesStats.scigov.numberSettingsCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Results</p>
                        <p className="text-lg font-bold text-purple-600">{courtCasesStats.scigov.resultsCount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <button 
                  onClick={() => navigate('/form-submissions')}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  View all
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity) => {
                    const Icon = getActivityIcon(activity.type);
                    return (
                      <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-150">
                        <div className={`p-2 rounded-full bg-gray-100 ${activity.color}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                          {activity.details && (
                            <p className="text-xs text-gray-600 mt-1">{activity.details}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <DocumentArrowUpIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <button 
                  onClick={() => handleQuickAction('create-form')}
                  className="w-full btn btn-primary"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Create New Form
                </button>
                <button 
                  onClick={() => handleQuickAction('add-user')}
                  className="w-full btn btn-secondary"
                >
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Add New User
                </button>
                <button 
                  onClick={() => handleQuickAction('create-task')}
                  className="w-full btn btn-secondary"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                  Create Task
                </button>
                <button 
                  onClick={() => handleQuickAction('view-submissions')}
                  className="w-full btn btn-secondary"
                >
                  <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
                  View Submissions
                </button>
                
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="card mt-6">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">System Status</h2>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {Object.entries(systemStatus).map(([service, status]) => (
                  <div key={service} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">
                      {service.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className={`badge ${
                      status === 'Online' ? 'badge-success' : 'badge-error'
                    }`}>
                      {status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 